import { Field, FieldProps, Form, Formik, FormikProps } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import useSignUp from "@/features/auth/sign-up/use-sign-up";
import { clearReferral } from "@/features/auth/sign-up/sign-up-referral";
import { useNavigate } from "react-router";
import { SignUpFormValues } from "@/features/auth/sign-up/sign-up-page";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  Box,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Link,
  Text,
} from "@chakra-ui/react";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import AppButton from "@/features/line-sheet-sets/app-button";

export interface SignUpStep4FormFields {
  agreeToAll: boolean;
  agreeToTermsOfUse: boolean;
  agreeToCollectPersonalInformation: boolean;
  agreeToProvidePersonalInformation: boolean;
  over14: boolean;
  agreeToMarketing: boolean;
}

interface SignUpStep4FormProps {
  initialValue: SignUpFormValues;
}

export default function SignUpStep4Form({
  initialValue,
}: SignUpStep4FormProps) {
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState("+1");
  const {
    fire: fireSignUp,
    isLoading: isSignUpLoading,
    data: signUpData,
    error: signUpError,
  } = useSignUp();
  const { error: showError } = useAppToasts();
  const { t, tTitle, tSentence } = useI18nHelper();
  const { withMessage, formT } = useYupHelper({
    ns: "auth",
    prefix: "sign_up_form",
  });

  const formikRef = useRef<FormikProps<SignUpStep4FormFields>>(null);

  useEffect(() => {
    if (signUpError) {
      showError(signUpError, {
        id: "SignUpForm",
      });
    }
  }, [showError, signUpError]);

  useEffect(() => {
    if (signUpData) {
      // 가입이 끝났으니 저장해둔 추천인을 비운다 — 같은 탭의 다음 가입에 따라붙지 않게.
      clearReferral();
      navigate("/auth/sign-up-pending", { replace: true });
    }
  }, [navigate, signUpData]);

  const validateSchema = useMemo(() => {
    return Yup.object({
      agreeToTermsOfUse: Yup.boolean().isTrue(),
      agreeToCollectPersonalInformation: Yup.boolean().isTrue(),
      agreeToProvidePersonalInformation: Yup.boolean().isTrue(),
      over14: Yup.boolean().isTrue(),
    });
  }, []);

  return (
    <Formik
      initialValues={initialValue}
      validationSchema={validateSchema}
      innerRef={formikRef}
      onSubmit={async (values, actions) => {
        // initialValues 로 누적 폼 값 전체를 받지만, 요청 바디는 스펙에 있는 필드만
        // 명시적으로 조립한다 — 스프레드하면 File(사업자등록증)이나 동의 체크값처럼
        // 바디에 없는 값까지 실려 나간다.
        await fireSignUp(
          {},
          {
            email: initialValue.email!,
            password: initialValue.password,
            name: initialValue.name,
            userInputedCompanyName: initialValue.userInputedCompanyName,
            countryCode: initialValue.countryCode,
            mobileNumber: initialValue.mobileNumber,
            agreeToMarketing: values.agreeToMarketing,
            // 3단계에서 필수로 받지만 스펙이 nullable이라 빈 값은 null로 보낸다.
            userInputedBusinessRegistrationNumber:
              initialValue.businessRegistrationNumber || null,
            businessRegistrationCertificateFileId:
              initialValue.businessRegistrationCertificateFileId,
            // 추천인은 선택 입력이라 빈 값이면 키 자체를 빼서 보낸다(서버 기본값 None).
            recommender: initialValue.recommender?.trim() || undefined,
          }
        );
        actions.setSubmitting(false);
      }}
    >
      {(props: FormikProps<SignUpStep4FormFields>) => {
        function setAgreement(name: string, value: any) {
          props.setValues((prev) => {
            const next = {
              ...prev,
              [name]: value,
            };

            if (name === "agreeToAll") {
              return {
                ...next,
                agreeToTermsOfUse: value,
                agreeToCollectPersonalInformation: value,
                agreeToProvidePersonalInformation: value,
                over14: value,
                agreeToMarketing: value,
              };
            } else {
              next.agreeToAll =
                next.agreeToTermsOfUse &&
                next.agreeToCollectPersonalInformation &&
                next.agreeToProvidePersonalInformation &&
                next.over14 &&
                next.agreeToMarketing;
              return next;
            }
          });
        }

        return (
          <Form>
            <Box display="flex" flexDirection="column" gap="32px">
              <Box display="flex" flexDirection="column" gap="16px">
                <Box
                  sx={{
                    padding: "16px",
                    borderRadius: "8px",
                    border: "1px solid #E6E6E6",
                    ...(props.values.agreeToAll
                      ? {
                          backgroundColor: "#F5F5F5",
                          borderColor: "#E6E6E6",
                        }
                      : {}),
                  }}
                >
                  <Field type={"checkbox"} name={"agreeToAll"}>
                    {(props: FieldProps<boolean, SignUpStep4FormFields>) => {
                      return (
                        <FormControl>
                          <AppCheckBox
                            name={t(
                              "auth:sign_up_form.field.agree_to_all.label"
                            )}
                            isChecked={props.field.value}
                            onChange={(value) => {
                              setAgreement(props.field.name, value);
                            }}
                          />
                        </FormControl>
                      );
                    }}
                  </Field>
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="16px"
                  marginLeft="2px"
                  marginRight="2px"
                >
                  <Field type={"checkbox"} name={"over14"}>
                    {(props: FieldProps<boolean, SignUpStep4FormFields>) => {
                      return (
                        <FormControl
                          as={Flex}
                          flexDirection={"column"}
                          isInvalid={
                            !!props.form.errors.over14 &&
                            props.form.touched.over14
                          }
                        >
                          <Flex flexDirection={"row"} gap={"8px"}>
                            <Box flexGrow={"1"}>
                              <AppCheckBox
                                name={`[${tTitle("auth:mandatory")}] ${t(
                                  "auth:sign_up_form.field.over_14.label"
                                )}`}
                                isChecked={props.field.value}
                                onChange={(value) =>
                                  setAgreement(props.field.name, value)
                                }
                              />
                            </Box>
                          </Flex>
                          <Flex
                            h="24px"
                            flexDirection="row"
                            alignItems={"center"}
                          >
                            <FormErrorMessage m="0" fontSize={"12px"}>
                              {
                                props.form.errors
                                  .agreeToProvidePersonalInformation
                              }
                            </FormErrorMessage>
                          </Flex>
                        </FormControl>
                      );
                    }}
                  </Field>
                  <Field type={"checkbox"} name={"agreeToTermsOfUse"}>
                    {(props: FieldProps<boolean, SignUpStep4FormFields>) => {
                      return (
                        <FormControl
                          as={Flex}
                          flexDirection={"column"}
                          isInvalid={
                            !!props.form.errors.agreeToTermsOfUse &&
                            props.form.touched.agreeToTermsOfUse
                          }
                        >
                          <Flex flexDirection={"row"} gap={"8px"}>
                            <Box flexGrow={"1"}>
                              <AppCheckBox
                                name={`[${tTitle("auth:mandatory")}] ${t(
                                  "auth:sign_up_form.field.agree_to_terms_of_use.label"
                                )}`}
                                isChecked={props.field.value}
                                onChange={(value) => {
                                  setAgreement(props.field.name, value);
                                }}
                              />
                            </Box>
                            <Link
                              flexShrink={"0"}
                              isExternal={true}
                              href={
                                "https://terms.pathrade.com/files/ko/UNOTradingCorp-Pathrade-Service-Agreement-v1.0-20231208.pdf"
                              }
                              fontSize={"12px"}
                            >
                              [{tTitle("view")}]
                            </Link>
                          </Flex>
                          <Flex
                            h="24px"
                            flexDirection="row"
                            alignItems={"center"}
                          >
                            <FormErrorMessage m="0" fontSize={"12px"}>
                              {props.form.errors.agreeToTermsOfUse}
                            </FormErrorMessage>
                          </Flex>
                        </FormControl>
                      );
                    }}
                  </Field>
                  <Field
                    type={"checkbox"}
                    name={"agreeToCollectPersonalInformation"}
                  >
                    {(props: FieldProps<boolean, SignUpStep4FormFields>) => {
                      return (
                        <FormControl
                          as={Flex}
                          flexDirection={"column"}
                          isInvalid={
                            !!props.form.errors
                              .agreeToCollectPersonalInformation &&
                            props.form.touched.agreeToCollectPersonalInformation
                          }
                        >
                          <Flex flexDirection={"row"} gap={"8px"}>
                            <Box flexGrow={"1"}>
                              <AppCheckBox
                                name={`[${tTitle("auth:mandatory")}] ${t(
                                  "auth:sign_up_form.field.agree_to_collect_personal_information.label"
                                )}`}
                                isChecked={props.field.value}
                                onChange={(value) =>
                                  setAgreement(props.field.name, value)
                                }
                              />
                            </Box>
                            <Link
                              flexShrink={"0"}
                              isExternal={true}
                              href={
                                "https://terms.pathrade.com/files/ko/UNOTradingCorp-Pathrade-Personal-information-collection-and-use-consent-form-v1.0-20231208.pdf"
                              }
                              fontSize={"12px"}
                            >
                              [{tTitle("view")}]
                            </Link>
                          </Flex>
                          <Flex
                            h="24px"
                            flexDirection="row"
                            alignItems={"center"}
                          >
                            <FormErrorMessage m="0" fontSize={"12px"}>
                              {
                                props.form.errors
                                  .agreeToCollectPersonalInformation
                              }
                            </FormErrorMessage>
                          </Flex>
                        </FormControl>
                      );
                    }}
                  </Field>
                  <Field
                    type={"checkbox"}
                    name={"agreeToProvidePersonalInformation"}
                  >
                    {(props: FieldProps<boolean, SignUpStep4FormFields>) => {
                      return (
                        <FormControl
                          as={Flex}
                          flexDirection={"column"}
                          isInvalid={
                            !!props.form.errors
                              .agreeToProvidePersonalInformation &&
                            props.form.touched.agreeToProvidePersonalInformation
                          }
                        >
                          <Flex flexDirection={"row"} gap={"8px"}>
                            <Box flexGrow={"1"}>
                              <AppCheckBox
                                name={`[${tTitle("auth:mandatory")}] ${t(
                                  "auth:sign_up_form.field.agree_to_provide_personal_information.label"
                                )}`}
                                isChecked={props.field.value}
                                onChange={(value) =>
                                  setAgreement(props.field.name, value)
                                }
                              />
                            </Box>
                            <Link
                              flexShrink={"0"}
                              isExternal={true}
                              href={
                                "https://terms.pathrade.com/files/ko/UNOTradingCorp-Pathrade-Consent-to-provide-personal-information-to-third-parties-v1.0-20231208.pdf"
                              }
                              fontSize={"12px"}
                            >
                              [{tTitle("view")}]
                            </Link>
                          </Flex>
                          <Flex
                            h="24px"
                            flexDirection="row"
                            alignItems={"center"}
                          >
                            <FormErrorMessage m="0" fontSize={"12px"}>
                              {
                                props.form.errors
                                  .agreeToProvidePersonalInformation
                              }
                            </FormErrorMessage>
                          </Flex>
                        </FormControl>
                      );
                    }}
                  </Field>
                  <Field type={"checkbox"} name={"agreeToMarketing"}>
                    {(props: FieldProps<boolean, SignUpStep4FormFields>) => {
                      return (
                        <FormControl
                          as={Flex}
                          flexDirection={"column"}
                          isInvalid={
                            !!props.form.errors.agreeToMarketing &&
                            props.form.touched.agreeToMarketing
                          }
                        >
                          <Flex flexDirection={"row"} gap={"8px"}>
                            <Box flexGrow={"1"}>
                              <AppCheckBox
                                name={`[${tTitle("auth:optional")}] ${t(
                                  "auth:sign_up_form.field.agree_to_marketing.label"
                                )}`}
                                isChecked={props.field.value}
                                onChange={(value) =>
                                  setAgreement(props.field.name, value)
                                }
                              />
                            </Box>
                            <Link
                              flexShrink={"0"}
                              isExternal={true}
                              href={
                                "https://terms.pathrade.com/files/ko/UNOTradingCorp-Pathrade-Consent-to-receive-advertising-information-and-use-personal-information-for-marketing-v1.0-20231208.pdf"
                              }
                              fontSize={"12px"}
                            >
                              [{tTitle("view")}]
                            </Link>
                          </Flex>
                          <Flex
                            h="24px"
                            flexDirection="row"
                            alignItems={"center"}
                          >
                            <FormErrorMessage m="0" fontSize={"12px"}>
                              {props.form.errors.agreeToMarketing}
                            </FormErrorMessage>
                          </Flex>
                        </FormControl>
                      );
                    }}
                  </Field>
                </Box>
              </Box>
              <AppButton
                variant={"gray"}
                size={"large"}
                width={"100%"}
                chakraProps={{ height: "40px" }}
                type={"submit"}
                isDisabled={!props.isValid}
                isLoading={props.isSubmitting}
              >
                <Text fontSize={"14px"}>
                  {tTitle("sign_up.do", { ns: "auth" })}
                </Text>
              </AppButton>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}
